import { cloneDeep } from '@shared/functions/clone-deep';
import { isPlainObject } from '@shared/functions/is-plain-object';
import {
    U2bColumnDefinition,
    U2bColumnDefinitionBrakePoint,
    U2bTableTextAlignments
} from '@core/components/layout/table/table.types';

type LegacyBreakpointColumnSettings = {
    [U2bColumnDefinitionBrakePoint.Desktop]: U2bColumnDefinition[];
    [U2bColumnDefinitionBrakePoint.Tablet]: U2bColumnDefinition[];
    [U2bColumnDefinitionBrakePoint.Mobile]: U2bColumnDefinition[];
};

export function migrateUserColumnDefinitionSettings(
    userColumnDefinitionSettings: U2bColumnDefinition[] | LegacyBreakpointColumnSettings,
): U2bColumnDefinition[] {
    if (!userColumnDefinitionSettings) {
        return [];
    }

    if (isPlainObject(userColumnDefinitionSettings)) {
        const legacySettings = userColumnDefinitionSettings as LegacyBreakpointColumnSettings;
        const migratedList = legacySettings[U2bColumnDefinitionBrakePoint.Desktop]
            .map(item => cloneDeep({
                ...item,
                isActive: {
                    [U2bColumnDefinitionBrakePoint.Desktop]: item.isActive,
                    [U2bColumnDefinitionBrakePoint.Tablet]: item.isActive,
                    [U2bColumnDefinitionBrakePoint.Mobile]: item.isActive,
                },
                alignment: {
                    [U2bColumnDefinitionBrakePoint.Desktop]: item.alignment,
                    [U2bColumnDefinitionBrakePoint.Tablet]: item.alignment,
                    [U2bColumnDefinitionBrakePoint.Mobile]: item.alignment,
                }
            }));

        for (const item of legacySettings[U2bColumnDefinitionBrakePoint.Tablet]) {
            const foundItem = migratedList.find(migratedItem => migratedItem.property === item.property);

            if (foundItem) {
                foundItem.isActive[U2bColumnDefinitionBrakePoint.Tablet] = item.isActive;
                foundItem.alignment[U2bColumnDefinitionBrakePoint.Tablet] = item.alignment;
            }
        }

        for (const item of legacySettings[U2bColumnDefinitionBrakePoint.Mobile]) {
            const foundItem = migratedList.find(migratedItem => migratedItem.property === item.property);

            if (foundItem) {
                foundItem.isActive[U2bColumnDefinitionBrakePoint.Mobile] = item.isActive;
                foundItem.alignment[U2bColumnDefinitionBrakePoint.Tablet] = item.alignment;
            }
        }

        return migratedList as unknown as U2bColumnDefinition[];
    }

    const list = userColumnDefinitionSettings as U2bColumnDefinition[];

    if (list.length && !isPlainObject(list[0].isActive)) {
        return list.map(item => ({
            ...item,
            isActive: {
                [U2bColumnDefinitionBrakePoint.Desktop]: item.isActive,
                [U2bColumnDefinitionBrakePoint.Tablet]: item.isActive,
                [U2bColumnDefinitionBrakePoint.Mobile]: item.isActive,
            },
            alignment: {
                [U2bColumnDefinitionBrakePoint.Desktop]: item.alignment,
                [U2bColumnDefinitionBrakePoint.Tablet]: item.alignment,
                [U2bColumnDefinitionBrakePoint.Mobile]: item.alignment,
            }
        })) as unknown as U2bColumnDefinition[];
    }

    return list;
}

export function applyColumnDefinitionDefaults(columnDefinitions: U2bColumnDefinition[]): U2bColumnDefinition[] {
    for (const columnDefinition of columnDefinitions) {
        columnDefinition.alignment = {
            [U2bColumnDefinitionBrakePoint.Desktop]: U2bTableTextAlignments.Left,
            [U2bColumnDefinitionBrakePoint.Tablet]: U2bTableTextAlignments.Left,
            [U2bColumnDefinitionBrakePoint.Mobile]: U2bTableTextAlignments.Left,
            ...(isPlainObject(columnDefinition.alignment) ? columnDefinition.alignment : {}),
        };
    }

    return columnDefinitions;
}

export function mergeColumnDefinitions(
    userColumnDefinitions: U2bColumnDefinition[],
    defaultColumnDefinitions: U2bColumnDefinition[],
): U2bColumnDefinition[] {
    const mergedColumnDefinitions: U2bColumnDefinition[] = [];

    for (const userColumnDefinition of (userColumnDefinitions || [])) {
        const foundColumnDefinition = defaultColumnDefinitions
            .find(defaultColumnDefinition => defaultColumnDefinition.property === userColumnDefinition.property);

        if (foundColumnDefinition) {
            mergedColumnDefinitions.push(Object.assign({}, foundColumnDefinition, userColumnDefinition));
        }
    }

    for (const defaultColumnDefinition of defaultColumnDefinitions) {
        const foundColumnDefinition = (userColumnDefinitions || [])
            .find(userColumnDefinition => userColumnDefinition.property === defaultColumnDefinition.property);

        if (!foundColumnDefinition) {
            mergedColumnDefinitions.push(defaultColumnDefinition);
        }
    }

    return applyColumnDefinitionDefaults(mergedColumnDefinitions);
}

export function sortColumnDefinitionsByPosition(
    columnDefinitions: U2bColumnDefinition[],
    breakpoint: U2bColumnDefinitionBrakePoint = U2bColumnDefinitionBrakePoint.Desktop,
): U2bColumnDefinition[] {
    return [...columnDefinitions].sort((left, right) => {
        const leftPosition = left.position?.[breakpoint];
        const rightPosition = right.position?.[breakpoint];

        if (leftPosition == null && rightPosition == null) {
            return 0;
        }
        if (leftPosition == null) {
            return 1;
        }
        if (rightPosition == null) {
            return -1;
        }

        return leftPosition - rightPosition;
    });
}

export function resolveEffectiveColumnDefinitions(
    defaultColumnDefinitions: U2bColumnDefinition[],
    userColumnDefinitionSettings?: U2bColumnDefinition[] | LegacyBreakpointColumnSettings | null,
): U2bColumnDefinition[] {
    if (!userColumnDefinitionSettings) {
        return defaultColumnDefinitions;
    }

    const migratedSettings = migrateUserColumnDefinitionSettings(userColumnDefinitionSettings);

    if (!migratedSettings.length) {
        return defaultColumnDefinitions;
    }

    return sortColumnDefinitionsByPosition(
        mergeColumnDefinitions(migratedSettings, defaultColumnDefinitions),
    );
}
